import { useMemo } from 'react';
import { useFetchData } from '../hooks/useFetchData';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { v2ServicePath } from '../config';
import { getMockJourneySankey } from './mockDemoData';

/** The only flow view the API accepts; any other value is a 400. */
export const JOURNEY_FLOW_PARAMS = Object.freeze({ flow_view: 'journeys' });

// ── hooks ──────────────────────────────────────────────────────────────────

/**
 * Fetch flow data (Sankey) for a service's Journeys view.
 * The endpoint serves only the Sankey in meta.flow; the per-agent list moved
 * to the Cost tab (Agents breakdown).
 */
export function useAgentList(selectedAppId, activeTab, isDemo) {
  const { selectedEnvironment } = useEnvironment();

  const endpoint = useMemo(() => {
    if (isDemo || !selectedAppId || activeTab !== 'agents') return null;
    return v2ServicePath(selectedAppId, '/agents');
  }, [selectedAppId, activeTab, isDemo]);

  // Module-level, so the reference is stable without a useMemo and a test can
  // assert the shipped value rather than a copy of it.
  const params = JOURNEY_FLOW_PARAMS;

  const { data, loading, error } = useFetchData({
    endpoint,
    params,
    enabled: !isDemo && !!selectedAppId && !!selectedEnvironment,
  });

  // useMemo already recomputes only when `data` changes, so a stable reference
  // is preserved across renders without manual ref caching.
  //
  // Demo mode fetches nothing, so without this the tab rendered empty for every
  // demo app. The mock is built in the same shape the API returns.
  const flowData = useMemo(
    () => (isDemo ? getMockJourneySankey(selectedAppId) : data?.meta?.flow?.sankey ?? null),
    [data, isDemo, selectedAppId],
  );

  return { flowData, loading, error };
}
